import { Component, Type } from '@angular/core';
import { Route } from '@angular/router';
import { RettighedsType } from '@core/auth/rettighed.model';
import { DefaultLayoutComponent } from '@core/layout/default/default-layout/default-layout.component';
import { SidenavLayoutComponent } from '@core/layout/sidenav/sidenav-layout/sidenav-layout.component';
import { NotFoundComponent } from '@core/pages/not-found/not-found.component';
import { UserProfileComponent } from '@core/pages/user-profile/user-profile.component';
import { BatchComponent } from '@features/batch/batch.component';
import { BrugereMainComponent } from '@features/brugere/brugere-main/brugere-main.component';
import { DokumenterAfsendelseComponent } from '@features/dokumenter/afsendelse/dokumenter-afsendelse.component';
import { DokumenterComponent } from '@features/dokumenter/dokumenter.component';
import { DriftsmeddelelserComponent } from '@features/driftsmeddelelser/driftsmeddelelser.component';
import { FilerComponent } from '@features/filer/filer.component';
import { HomeComponent } from '@features/home/home.component';
import { KonverteringMainComponent } from '@features/konvertering/konvertering-main/konvertering-main.component';
import { KonverteringShowComponent } from '@features/konvertering/konvertering-show/konvertering-show.component';
import { KunderComponent } from '@features/kunder/kunder.component';
import { LetpensiondkBestyrelseComponent } from '@features/letpensiondk/letpensiondk-bestyrelse/letpensiondk-bestyrelse.component';
import { LetpensiondkDokumenterComponent } from '@features/letpensiondk/letpensiondk-dokumenter/letpensiondk-dokumenter.component';
import { LetpensiondkJobopslagComponent } from '@features/letpensiondk/letpensiondk-jobopslag/letpensiondk-jobopslag.component';
import { LetpensiondkMedarbejdereComponent } from '@features/letpensiondk/letpensiondk-medarbejdere/letpensiondk-medarbejdere.component';
import { LetregisterComponent } from '@features/letregister/letregister.component';
import { LogComponent } from '@features/log/log.component';
import { OpkraevningsListerComponent } from '@features/opkraevningslister/opkraevningslister.component';
import { PengeinstitutterComponent } from '@features/pengeinstitutter/pengeinstitutter.component';
import { PensionsudbydereComponent } from '@features/pensionsudbydere/pensionsudbydere.component';
import { ProduktlisterComponent } from '@features/produktlister/produktlister.component';
import { RaadgivereComponent } from '@features/raadgivere/raadgivere.component';
import { RisikoopkraevningLeverandoerComponent } from '@features/risikoopkraevning/risikoopkraevning-leverandoer/risikoopkraevning-leverandoer.component';
import { RisikoopkraevningOpkraevningerComponent } from '@features/risikoopkraevning/risikoopkraevning-opkraevninger/risikoopkraevning-opkraevninger.component';
import { RisikoopkraevningUdestaaendeComponent } from '@features/risikoopkraevning/risikoopkraevning-udestaaende/risikoopkraevning-udestaaende.component';
import { RollerMainComponent } from '@features/roller/roller-main/roller-main.component';
import { SsbKonfigurationComponent } from '@features/ssb-konfiguration/ssb-konfiguration.component';
import { SletRevisionComponent } from '@features/support/slet-revision/slet-revision.component';
import { VendUnderskriftComponent } from '@features/support/vend-underskrift/vend-underskrift.component';
import { TestComponent } from '@shared/test/test.component';

export interface RouteData {
  layout?: Type<Component>;
  rettighed?: RettighedsType;
}

interface CustomRoute extends Route {
  data?: RouteData;
  children?: CustomRoute[];
}

export const routes: CustomRoute[] = [
  {
    path: '',
    pathMatch: 'full',
    title: 'Administrationsportal',
    component: HomeComponent,
    data: {
      rettighed: RettighedsType.AppAdmin,
    },
  },
  {
    path: 'profile',
    title: 'Adminportal - Profil',
    component: UserProfileComponent,
  },
  {
    path: 'brugeradm',
    pathMatch: 'full',
    title: 'Adminportal - Brugeradministration',
    redirectTo: '/brugeradm/brugere',
  },
  {
    path: 'brugeradm/brugere',
    title: 'Adminportal - Brugere',
    component: BrugereMainComponent,
    data: {
      rettighed: RettighedsType.AdminBruger,
    },
  },
  {
    path: 'brugeradm/roller',
    title: 'Adminportal - Roller',
    component: RollerMainComponent,
    data: {
      rettighed: RettighedsType.AdminRolle,
    },
  },
  {
    path: 'support',
    title: 'Adminportal - Forretningssupport',
    pathMatch: 'full',
    redirectTo: 'support/sletrevision',
  },
  {
    path: 'support/sletrevision',
    title: 'Adminportal - Slet revision',
    component: SletRevisionComponent,
    data: {
      rettighed: RettighedsType.AdminSupportSletRevision,
    },
  },
  {
    path: 'support/vendtilfysiskunderskrift',
    title: 'Adminportal - Vend til fysisk underskrift',
    component: VendUnderskriftComponent,
    data: {
      rettighed: RettighedsType.AdminSupportVendTilFysiskUnderskrift,
    },
  },
  {
    path: 'log',
    title: 'Adminportal - Log',
    component: LogComponent,
    data: {
      rettighed: RettighedsType.AdminLog,
    },
  },
  {
    path: 'pengeinstitutter',
    title: 'Adminportal - Pengeinstitutter',
    component: PengeinstitutterComponent,
    data: { rettighed: RettighedsType.AdminPengeinstitutter },
  },
  {
    path: 'pensionsudbydere',
    title: 'Adminportal - Pensionsudbydere',
    component: PensionsudbydereComponent,
    data: { rettighed: RettighedsType.AdminPensionsudbydere },
  },
  {
    path: 'raadgivere',
    title: 'Adminportal - Rådgivere',
    component: RaadgivereComponent,
    data: { rettighed: RettighedsType.AdminRaadgivere },
  },
  {
    path: 'dokumenter',
    title: 'Adminportal - Dokumenter',
    redirectTo: '/dokumenter/dokumenter',
    pathMatch: 'full',
    data: { rettighed: RettighedsType.AdminDokumenter },
  },
  {
    path: 'dokumenter/dokumenter',
    title: 'Adminportal - Dokumenter',
    component: DokumenterComponent,
    data: { rettighed: RettighedsType.AdminDokumenter },
  },
  {
    path: 'dokumenter/afsendelse',
    title: 'Adminportal - Dokumentafsendelse',
    component: DokumenterAfsendelseComponent,
    data: { rettighed: RettighedsType.AdminDokumenter },
  },
  {
    path: 'kunder',
    title: 'Adminportal - Kunder',
    component: KunderComponent,
    data: { rettighed: RettighedsType.AdminKunder },
  },
  {
    path: 'opkraevningslister',
    title: 'Adminportal - Opkrævningslister',
    component: OpkraevningsListerComponent,
    data: { rettighed: RettighedsType.AdminOpkraevningslister },
  },
  {
    path: 'produktlister',
    title: 'Adminportal - Produktlister',
    component: ProduktlisterComponent,
    data: { rettighed: RettighedsType.AdminProduktlister },
  },
  {
    path: 'risikoopkraevning',
    title: 'Adminportal - Risikoopkrævning',
    redirectTo: '/risikoopkraevning/opkraevninger',
    pathMatch: 'full',
    data: { rettighed: RettighedsType.AdminRisikoopkraevning },
  },
  {
    path: 'risikoopkraevning/opkraevninger',
    title: 'Adminportal - Risikoopkrævninger',
    component: RisikoopkraevningOpkraevningerComponent,
    data: { rettighed: RettighedsType.AdminRisikoopkraevning },
  },
  {
    path: 'risikoopkraevning/udestaaende',
    title: 'Adminportal - Udestående opkrævninger',
    component: RisikoopkraevningUdestaaendeComponent,
    data: { rettighed: RettighedsType.AdminRisikoopkraevning },
  },
  {
    path: 'risikoopkraevning/leverandoer',
    title: 'Adminportal - Leverandøropkrævninger',
    component: RisikoopkraevningLeverandoerComponent,
    data: { rettighed: RettighedsType.AdminRisikoopkraevning },
  },
  {
    path: 'batch',
    title: 'Adminportal - Batch',
    component: BatchComponent,
    data: { rettighed: RettighedsType.AdminBatch },
  },
  {
    path: 'driftsmeddelelser',
    title: 'Adminportal - Driftsmeddelelser',
    component: DriftsmeddelelserComponent,
    data: { rettighed: RettighedsType.AdminDriftsmeddelelser },
  },
  {
    path: 'ssbkonfiguration',
    title: 'Adminportal - SSB Konfiguration',
    component: SsbKonfigurationComponent,
    data: { rettighed: RettighedsType.AdminSsbKonfiguration },
  },
  {
    path: 'filer',
    title: 'Adminportal - Filer',
    component: FilerComponent,
    data: { rettighed: RettighedsType.AdminFiler },
  },
  {
    path: 'letpensiondk',
    title: 'Adminportal - letpension.dk',
    pathMatch: 'full',
    redirectTo: 'letpensiondk/dokumenter',
  },
  {
    path: 'letpensiondk/dokumenter',
    title: 'Adminportal - letpension.dk - Dokumenter',
    component: LetpensiondkDokumenterComponent,
    data: {
      rettighed: RettighedsType.AdminLetpensionDk,
    },
  },
  {
    path: 'letpensiondk/jobopslag',
    title: 'Adminportal - letpension.dk - Jobopslag',
    component: LetpensiondkJobopslagComponent,
    data: {
      rettighed: RettighedsType.AdminLetpensionDk,
    },
  },
  {
    path: 'letpensiondk/medarbejdere',
    title: 'Adminportal - letpension.dk - Medarbejdere',
    component: LetpensiondkMedarbejdereComponent,
    data: {
      rettighed: RettighedsType.AdminLetpensionDk,
    },
  },
  {
    path: 'letpensiondk/bestyrelse',
    title: 'Adminportal - letpension.dk - Bestyrelse',
    component: LetpensiondkBestyrelseComponent,
    data: {
      rettighed: RettighedsType.AdminLetpensionDk,
    },
  },
  {
    path: 'konvertering',
    title: 'Konvertering',
    component: KonverteringMainComponent,
    data: {
      rettighed: RettighedsType.KonverteringMenuPunkt,
    },
  },
  {
    path: 'konvertering/konverteringdata/:id',
    title: 'Konvertering data',
    component: KonverteringShowComponent,
    data: {
      layout: DefaultLayoutComponent,
      rettighed: RettighedsType.KonverteringUdtraek,
    },
  },
  {
    path: 'letregister',
    title: 'Adminportal - Letregister',
    component: LetregisterComponent,
    data: { rettighed: RettighedsType.AdminLetregister },
  },
  {
    path: 'test',
    component: TestComponent,
    data: {
      layout: SidenavLayoutComponent,
      rettighed: RettighedsType.DeveloperMode,
    },
  },
  {
    path: '**',
    component: NotFoundComponent,
  },
];
